import { useCallback, useEffect } from 'react';
import type { FC } from 'react';
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useBreakpointValue,
} from '@chakra-ui/react';
import { isModernBrowser } from '../../../utils/utils';
import { GetIcon } from '../../icons';
import { usePhotoGridStore, usePhotoGridStoreMutation } from '../usePhotoStore';
import { AssetSection } from './asset-section/AssetSection';
import { ThumbnailSection } from './thumbnail-section/ThumbnailSection';

export const Zoom: FC<{ thumbnailDisabled?: boolean }> = ({ thumbnailDisabled = false }) => {
    const { data } = usePhotoGridStore();
    const photoGridMutation = usePhotoGridStoreMutation();
    const isKeyboardNavigationEnabled = useBreakpointValue<boolean>({ base: false, md: true });

    const handleKeyPress = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                photoGridMutation.previousAsset();
            }
            if (event.key === 'ArrowRight') {
                photoGridMutation.nextAsset();
            }
        },
        [photoGridMutation],
    );

    useEffect(() => {
        if (!isKeyboardNavigationEnabled) {
            return undefined;
        }
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [isKeyboardNavigationEnabled, handleKeyPress]);

    const handleOnClose = useCallback(() => {
        photoGridMutation.mutate({ isZoomModalOpen: false });
    }, [photoGridMutation]);

    return (
        <Modal
            isOpen={!!data?.isZoomModalOpen}
            onClose={handleOnClose}
            scrollBehavior="inside"
            variant={isModernBrowser ? 'fullscreen' : 'fullscreen_legacy'}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader p={4}>
                    <div className="flex justify-end">
                        <button className="flex" type="button" onClick={handleOnClose}>
                            <GetIcon name="close" size="md" />
                        </button>
                    </div>
                </ModalHeader>
                <ModalBody p={0}>
                    <article
                        className="flex h-full w-full flex-col space-y-4 pb-4"
                        data-testid="photo-grid-zoom-overlay"
                    >
                        <AssetSection />
                        {!thumbnailDisabled && <ThumbnailSection />}
                    </article>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
